<script setup lang="ts">
import { UserIcon, EyeIcon, EyeOffIcon } from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { useField, useForm } from 'vee-validate'
import { ref, onMounted, nextTick } from 'vue'

import BaseSpinner from '@/components/ui/BaseSpinner.vue'
import { useAuthStore } from '@/stores/authStore'
import {
  validatePassword,
  validateCurrentPassword,
  createConfirmPasswordValidator,
} from '@/utils/passwordValidation'
import { validateProfileName } from '@/utils/profileValidation'

interface Emits {
  success: []
  close: []
}

const emit = defineEmits<Emits>()

// Auth store
const authStore = useAuthStore()
const { user, isLoading, error } = storeToRefs(authStore)

// Form handling
const { handleSubmit } = useForm()

// Profile name field
const {
  value: profileName,
  errorMessage: profileNameError,
  meta: profileNameMeta,
} = useField('profileName', validateProfileName)

// Password change toggle
const showPasswordSection = ref(false)

// Password visibility toggles
const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

// Password fields
const {
  value: currentPassword,
  errorMessage: currentPasswordError,
  meta: currentPasswordMeta,
} = useField('currentPassword', (value: string) => {
  if (!showPasswordSection.value) return true
  return validateCurrentPassword(value)
})

const {
  value: newPassword,
  errorMessage: newPasswordError,
  meta: newPasswordMeta,
} = useField('newPassword', (value: string) => {
  if (!showPasswordSection.value) return true
  return validatePassword(value)
})

const {
  value: confirmNewPassword,
  errorMessage: confirmNewPasswordError,
  meta: confirmNewPasswordMeta,
} = useField('confirmNewPassword', (value: string) => {
  if (!showPasswordSection.value) return true
  return createConfirmPasswordValidator(() => newPassword.value)(value)
})

// Set initial value from current user
if (user.value?.name) {
  profileName.value = user.value.name
}

// Form submission
const onSubmit = handleSubmit(async (values) => {
  authStore.clearError()

  const profileData = {
    profileName: values.profileName?.trim() || null,
    ...(showPasswordSection.value && values.currentPassword && values.newPassword
      ? {
          passwordCurrent: values.currentPassword,
          password: values.newPassword,
        }
      : {}),
  }

  const success = await authStore.editProfile(profileData)

  if (success) {
    emit('success')
    emit('close')
  }
})

// Focus management
const profileNameInputRef = ref<HTMLInputElement | null>(null)

// Helper functions
const togglePasswordSection = () => {
  showPasswordSection.value = !showPasswordSection.value
  // Clear password fields when hiding section
  if (!showPasswordSection.value) {
    currentPassword.value = ''
    newPassword.value = ''
    confirmNewPassword.value = ''
  }
}

const toggleCurrentPasswordVisibility = () => {
  showCurrentPassword.value = !showCurrentPassword.value
}

const toggleNewPasswordVisibility = () => {
  showNewPassword.value = !showNewPassword.value
}

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

onMounted(() => {
  nextTick(() => {
    if (profileNameInputRef.value) {
      profileNameInputRef.value.focus()
    }
  })
})
</script>

<template>
  <!-- Add live region for form status announcements -->
  <div id="profile-edit-status" class="sr-only" aria-live="polite" aria-atomic="true">
    <span v-if="isLoading">Updating profile, please wait...</span>
    <span v-if="error">Error: {{ error }}</span>
  </div>

  <form @submit.prevent="onSubmit" class="space-y-4" novalidate>
    <!-- Profile Name field -->
    <div>
      <label for="profile-name" class="block text-sm font-medium text-gray-700 mb-2">
        Profile Name (visible to others) <span class="text-red-500" aria-label="required">*</span>
      </label>
      <input
        id="profile-name"
        ref="profileNameInputRef"
        v-model="profileName"
        type="text"
        autocomplete="name"
        required
        maxlength="20"
        class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors"
        :class="{
          'border-red-300 focus:ring-red-500': profileNameError,
          'border-green-300 focus:ring-green-500': profileNameMeta.valid && profileNameMeta.touched,
          'border-gray-300 focus:ring-primary-500':
            !profileNameError && (!profileNameMeta.valid || !profileNameMeta.touched),
        }"
        placeholder="Enter your profile name"
        :disabled="isLoading"
        :aria-invalid="profileNameError ? 'true' : 'false'"
        :aria-describedby="profileNameError ? 'profile-name-error' : 'profile-name-help'"
      />
      <div v-if="!profileNameError" id="profile-name-help" class="sr-only">
        Enter your profile name (2-20 characters)
      </div>
      <div
        v-if="profileNameError"
        id="profile-name-error"
        class="mt-1 text-sm text-red-600"
        role="alert"
      >
        {{ profileNameError }}
      </div>
    </div>

    <!-- Change Password Section -->
    <div class="pt-2">
      <button
        type="button"
        @click="togglePasswordSection"
        :class="[
          // Layout & sizing
          'flex items-center text-sm p-1',
          // Colors & interaction
          'text-primary-600 hover:text-primary-700 hover:cursor-pointer',
          // Focus state
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
          // Shape
          'rounded-md',
        ]"
      >
        {{ showPasswordSection ? 'Cancel Password Change' : 'Change Password' }}
      </button>

      <div v-if="showPasswordSection" class="mt-4 space-y-4">
        <!-- Current Password field -->
        <div>
          <label for="current-password" class="block text-sm font-medium text-gray-700 mb-2">
            Current Password <span class="text-red-500" aria-label="required">*</span>
          </label>
          <div class="relative">
            <input
              id="current-password"
              v-model="currentPassword"
              :type="showCurrentPassword ? 'text' : 'password'"
              autocomplete="current-password"
              required
              class="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 transition-colors"
              :class="{
                'border-red-300 focus:ring-red-500': currentPasswordError,
                'border-green-300 focus:ring-green-500':
                  currentPasswordMeta.valid && currentPasswordMeta.touched,
                'border-gray-300 focus:ring-primary-500':
                  !currentPasswordError &&
                  (!currentPasswordMeta.valid || !currentPasswordMeta.touched),
              }"
              placeholder="Enter your current password"
              :disabled="isLoading"
              :aria-invalid="currentPasswordError ? 'true' : 'false'"
              :aria-describedby="
                currentPasswordError ? 'current-password-error' : 'current-password-help'
              "
            />
            <button
              type="button"
              @click="toggleCurrentPasswordVisibility"
              class="absolute inset-y-0 right-0 flex items-center pr-3 hover:cursor-pointer"
              :aria-label="showCurrentPassword ? 'Hide password' : 'Show password'"
            >
              <EyeOffIcon
                v-if="showCurrentPassword"
                class="w-4 h-4 text-gray-400"
                aria-hidden="true"
              />
              <EyeIcon v-else class="w-4 h-4 text-gray-400" aria-hidden="true" />
            </button>
          </div>
          <div v-if="!currentPasswordError" id="current-password-help" class="sr-only">
            Enter your current password
          </div>
          <div
            v-if="currentPasswordError"
            id="current-password-error"
            class="mt-1 text-sm text-red-600"
            role="alert"
          >
            {{ currentPasswordError }}
          </div>
        </div>

        <!-- New Password field -->
        <div>
          <label for="new-password" class="block text-sm font-medium text-gray-700 mb-2">
            New Password <span class="text-red-500" aria-label="required">*</span>
          </label>
          <div class="relative">
            <input
              id="new-password"
              v-model="newPassword"
              :type="showNewPassword ? 'text' : 'password'"
              autocomplete="new-password"
              required
              class="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 transition-colors"
              :class="{
                'border-red-300 focus:ring-red-500': newPasswordError,
                'border-green-300 focus:ring-green-500':
                  newPasswordMeta.valid && newPasswordMeta.touched,
                'border-gray-300 focus:ring-primary-500':
                  !newPasswordError && (!newPasswordMeta.valid || !newPasswordMeta.touched),
              }"
              placeholder="Enter your new password"
              :disabled="isLoading"
              :aria-invalid="newPasswordError ? 'true' : 'false'"
              :aria-describedby="newPasswordError ? 'new-password-error' : 'new-password-help'"
            />
            <button
              type="button"
              @click="toggleNewPasswordVisibility"
              class="absolute inset-y-0 right-0 flex items-center pr-3 hover:cursor-pointer"
              :aria-label="showNewPassword ? 'Hide password' : 'Show password'"
            >
              <EyeOffIcon v-if="showNewPassword" class="w-4 h-4 text-gray-400" aria-hidden="true" />
              <EyeIcon v-else class="w-4 h-4 text-gray-400" aria-hidden="true" />
            </button>
          </div>
          <div v-if="!newPasswordError" id="new-password-help" class="sr-only">
            Enter your new password (at least 8 characters)
          </div>
          <div
            v-if="newPasswordError"
            id="new-password-error"
            class="mt-1 text-sm text-red-600"
            role="alert"
          >
            {{ newPasswordError }}
          </div>
        </div>

        <!-- Confirm New Password field -->
        <div>
          <label for="confirm-new-password" class="block text-sm font-medium text-gray-700 mb-2">
            Confirm New Password <span class="text-red-500" aria-label="required">*</span>
          </label>
          <div class="relative">
            <input
              id="confirm-new-password"
              v-model="confirmNewPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              autocomplete="new-password"
              required
              class="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 transition-colors"
              :class="{
                'border-red-300 focus:ring-red-500': confirmNewPasswordError,
                'border-green-300 focus:ring-green-500':
                  confirmNewPasswordMeta.valid && confirmNewPasswordMeta.touched,
                'border-gray-300 focus:ring-primary-500':
                  !confirmNewPasswordError &&
                  (!confirmNewPasswordMeta.valid || !confirmNewPasswordMeta.touched),
              }"
              placeholder="Confirm your new password"
              :disabled="isLoading"
              :aria-invalid="confirmNewPasswordError ? 'true' : 'false'"
              :aria-describedby="
                confirmNewPasswordError ? 'confirm-new-password-error' : 'confirm-new-password-help'
              "
            />
            <button
              type="button"
              @click="toggleConfirmPasswordVisibility"
              class="absolute inset-y-0 right-0 flex items-center pr-3 hover:cursor-pointer"
              :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
            >
              <EyeOffIcon
                v-if="showConfirmPassword"
                class="w-4 h-4 text-gray-400"
                aria-hidden="true"
              />
              <EyeIcon v-else class="w-4 h-4 text-gray-400" aria-hidden="true" />
            </button>
          </div>
          <div v-if="!confirmNewPasswordError" id="confirm-new-password-help" class="sr-only">
            Confirm your new password
          </div>
          <div
            v-if="confirmNewPasswordError"
            id="confirm-new-password-error"
            class="mt-1 text-sm text-red-600"
            role="alert"
          >
            {{ confirmNewPasswordError }}
          </div>
        </div>
      </div>
    </div>

    <!-- Error message -->
    <div
      v-if="error"
      class="p-3 bg-red-50 border border-red-200 rounded-md"
      role="alert"
      aria-live="polite"
    >
      <p class="text-sm text-red-600">{{ error }}</p>
    </div>

    <!-- Submit button -->
    <button
      type="submit"
      :disabled="isLoading"
      :class="[
        // Layout & sizing
        'w-full flex items-center justify-center px-4 py-2',
        // Colors & states
        'bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white',
        // Typography & effects
        'font-medium rounded-md transition-colors',
        // Focus & interaction
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 hover:cursor-pointer',
      ]"
    >
      <UserIcon v-if="!isLoading" class="w-4 h-4 mr-2" aria-hidden="true" />
      <BaseSpinner v-else class="mr-2" aria-hidden="true" />
      {{ isLoading ? 'Updating Profile...' : 'Update Profile' }}
    </button>
    <div v-if="isLoading" id="profile-edit-loading-status" class="sr-only">
      Please wait while we update your profile
    </div>
  </form>
</template>
